/**
 * Team profile pages will be handled by Plasmic.
 * Commenting out this file for now.
 */

/*
import clsx from "clsx";
import { GetStaticPaths, GetStaticProps } from "next";
import Head from "next/head";
import { Seomatic } from "react-seomatic";

import { PLASMIC } from "@abnormal/plasmic-init";
import PageTitle from "@components/PageTitle";
import Profile from "@components/Profile";
import ProfileGrid from "@components/ProfileGrid";
import useSetHeaderTheme from "@hooks/useSetHeaderTheme";
import { PreviewContent } from "@interfaces/PreviewContent.interface";
import { returnStaticProps } from "@lib/returnStaticProps";
import craft from "@pages/api/craft";
import QueryProfile from "@queries/QueryProfile.graphql";
import QuerySlugs from "@queries/QuerySlugs.graphql";

export const getStaticPaths: GetStaticPaths = async (context) => {
	// Fetch every possible path
	const response = await craft(QuerySlugs, { section: "profiles", limit: 10 });

	const paths = [];

	response.entries.map((profile) => {
		console.log(`Generating /team/${profile.slug}`);

		paths.push({
			params: { slug: profile.slug }
		});
	});

	return { paths, fallback: "blocking" };
};

export const getStaticProps: GetStaticProps = async ({
	params,
	previewData: nextPreviewData,
	preview
}) => {
	// For Craft preview capability
	const previewData: PreviewContent = nextPreviewData as PreviewContent;

	// Construct uri from the slug array
	const { slug } = params;

	// What is the uri
  const uri = `/about/team/${slug}`;
  
  const plasmicData = await PLASMIC.maybeFetchComponentData([
    "Footer"
  ], {
    // deferChunks: true,
  });
  if (!plasmicData) {
    throw new Error("No Plasmic design found");
  }

	// Fetch the page's data
	const response = await craft(
		QueryProfile,
		{ slug },
		preview ? previewData.token : null
	);

	const {
		pageEntry,
		profile,
		profiles,
		primaryNavigation,
		primaryCallToActions,
		persistentNav
	} = response;

	// Return props if entry exists, otherwise check Retour for redirects or 404
	const returnResponse = await returnStaticProps(
		{
			navigation: {
				primaryNavigation,
				primaryCallToActions
			},
			pageEntry,
			persistentNav,
			profile,
      profiles,
      plasmicData
		},
		!profile,
		uri
	);

	return returnResponse;
};

interface IProps {
	pageEntry: any;
	profile: any;
	profiles: any[];
}

function Page({ pageEntry, profile, profiles }: IProps): JSX.Element {
	useSetHeaderTheme("dark");

	const profileType = profile.profileType[0];
	const size = profileType?.thumbnailSize ? profileType.thumbnailSize : "sm";
	const aspectRatio = "landscape";
	const image =
		aspectRatio === "square" ? profile.imageSquare[0] : profile.image[0];

	return (
		<>
			<Seomatic Head={Head} {...profile.seomatic} />
			<PageTitle headingTag="h2" target={{ element: pageEntry, type: "entry" }}>
				{profileType?.title} /
			</PageTitle>
			<div className={clsx("ProfileEntry__profile", "pt-7.5 mb-15 lg:mb-15")}>
				<Profile
					heading={profile.title}
					image={image}
					imageBlur={profile.imageBlur[0]}
					socialLinks={[
						{ label: "LinkedIn", url: profile.linkedinUrl, type: "icon" },
						{ label: "Github", url: profile.githubUrl, type: "button" },
						{ label: "Instagram", url: profile.instagramUrl, type: "button" }
					]}
					role={profile.role}
					biography={profile.biography}
					aspectRatio={aspectRatio}
				/>
			</div>
			<div className={clsx("ProfileEntry__profileGrid", "pb-30")}>
				<ProfileGrid
					profiles={profiles.filter(
						(p) =>
							p.profileType[0]?.id === profileType?.id && p.id !== profile.id
					)}
					size={size}
				></ProfileGrid>
			</div>
		</>
	);
}

export default Page;
*/

import { GetStaticPaths, GetStaticProps } from "next";
import Head from "next/head";
import { useRouter } from "next/router";
import { Seomatic } from "react-seomatic";

import { PLASMIC } from "@abnormal/plasmic-init";
import useSetHeaderTheme from "@hooks/useSetHeaderTheme";
import { PreviewContent } from "@interfaces/PreviewContent.interface";
import { returnStaticProps } from "@lib/returnStaticProps";
import craft from "@pages/api/craft";
import {
  ComponentRenderData,
  extractPlasmicQueryData,
  PlasmicComponent,
  PlasmicRootProvider
} from "@plasmicapp/loader-nextjs";
import QueryProfile from "@queries/QueryProfile.graphql";
import QuerySlugs from "@queries/QuerySlugs.graphql";

export const getStaticPaths: GetStaticPaths = async () => {
  // Fetch every possible path
  const response = await craft(QuerySlugs, { 
    section: "profiles",
    limit: 20
  });

  const paths = [];

  response.entries.map((profile) => {
    console.log(`Generating /about/team/${profile.slug}`);

    paths.push({
      params: { slug: profile.slug }
    });
  });

  return { paths, fallback: "blocking" };
};

export const getStaticProps: GetStaticProps = async ({
  params,
  previewData: nextPreviewData,
  preview
}) => {
  // For Craft preview capability
  const previewData: PreviewContent = nextPreviewData as PreviewContent;

  // Get the slug
  const { slug } = params;

  // Construct the uri
  const uri = `/about/team/${slug}`;

  // Fetch the Plasmic component for this path
  const plasmicData = await PLASMIC.maybeFetchComponentData([
    uri, 
    "Footer"
  ], {
    // deferChunks: true,
  });

  if (!plasmicData) {
    console.error(`No Plasmic data found for ${uri}`);
    return { props: {} };
  }

  // Fetch profile data from Craft
  const response = await craft(
    QueryProfile,
    { slug },
    preview ? previewData.token : null
  );

  const {
    pageEntry,
    profile,
    profiles,
    primaryNavigation,
    primaryCallToActions,
    persistentNav
  } = response;

  // If no profile found, 404
  if (!profile) {
    return {
      notFound: true
    };
  }

  // Process the Plasmic queries
  const queryCache = await extractPlasmicQueryData(
    <PlasmicRootProvider 
      loader={PLASMIC} 
      prefetchedData={plasmicData}
    >
      <PlasmicComponent component={plasmicData.entryCompMetas[0].displayName} />
    </PlasmicRootProvider>
  );

  // Return props if entry exists, otherwise check Retour for redirects or 404
  const returnResponse = await returnStaticProps(
    {
      navigation: {
        primaryNavigation,
        primaryCallToActions
      },
      pageEntry,
      persistentNav,
      profile,
      profiles,
      plasmicData,
      queryCache
    },
    !profile,
    uri
  );

  return returnResponse;
};

interface IProps {
  profile: Record<string, unknown>;
  plasmicData: ComponentRenderData;
  queryCache: Record<string, unknown>;
}

export default function Page({
  profile,
  plasmicData,
  queryCache
}: IProps): JSX.Element {
  const router = useRouter();
  
  // Set the header theme
  useSetHeaderTheme("dark");

  if (!plasmicData || plasmicData.entryCompMetas.length === 0) {
    return (
      <div>Error: No Plasmic component found for this profile</div>
    );
  }

  const pageMeta = plasmicData.entryCompMetas[0];
  
  return (
    <PlasmicRootProvider
      loader={PLASMIC}
      prefetchedData={plasmicData}
      prefetchedQueryData={queryCache}
      pageQuery={router.query}
      pageParams={pageMeta.params}
    >
      {profile && profile.seomatic && (
        <Seomatic Head={Head} {...(profile.seomatic as Record<string, string>)} />
      )}
      <PlasmicComponent component={pageMeta.displayName} />
    </PlasmicRootProvider>
  );
}
